import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./referralPhysicians.css";
import AddPhysicianDialog from "./AddPhysicianDialog";
import { errorBody, physicianSubline } from "./physicianForm";
import { searchPhysicians, setPhysicianActive } from "./referralPhysiciansApi";

// Admins only (owner item 3, 2026-10-19): every referral physician, active and
// deactivated, with Edit / Deactivate / Reactivate. The server enforces the
// rule (403 for anyone else); this view is only offered to admins.
// Deactivating hides the physician from the Referrals list and search; past
// orders keep the name. Nothing is deleted.

export default function ManagePhysiciansDialog({ open, onClose, access = {}, onChanged, onAdded, confirmFn }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const seq = useRef(0);
  const ask = confirmFn || ((text) => typeof window !== "undefined" && window.confirm(text));

  const load = useCallback(async (term) => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const found = await searchPhysicians(term, { includeInactive: true, limit: 100 });
      if (mine === seq.current) setRows(found);
    } catch (e) {
      if (mine === seq.current) setMessage({ err: true, text: "Could not load the list." });
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(t);
  }, [open, q, load]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setMessage(null);
      setEditing(null);
      setAdding(false);
    }
  }, [open]);

  const toggle = async (row) => {
    const active = !row.active;
    if (!active && !ask(`Deactivate ${row.doctor_name}? It will no longer appear in the Referrals list. Past orders keep the name.`)) return;
    setBusyId(row.id);
    setMessage(null);
    try {
      const res = await setPhysicianActive(row.id, active);
      setMessage({ err: false, text: res && res.message ? res.message : "Saved." });
      await load(q.trim());
      if (onChanged) onChanged();
    } catch (err) {
      const b = errorBody(err) || {};
      setMessage({ err: true, text: b.message || "Could not save. Please try again." });
    } finally {
      setBusyId(null);
    }
  };

  const saved = async (row, opts = {}) => {
    const wasAdding = adding;
    setEditing(null);
    setAdding(false);
    if (wasAdding && row && onAdded) onAdded(row, opts);   // the parent adds it to favourites
    setMessage({ err: false, text: row ? `Saved ${row.doctor_name}.` : "Saved." });
    await load(q.trim());
    if (onChanged) onChanged();
  };

  return (
    <>
      <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16 } }}
              aria-labelledby="rp-manage-title">
        <DialogContent>
          <div className="rp">
            <div className="rp-head">
              <div>
                <h2 id="rp-manage-title" className="rp-title">Referral physicians</h2>
                <p className="rp-sub">Edit details, or deactivate a physician who should no longer be offered.</p>
              </div>
              <IconButton onClick={onClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </div>
            <div className="rp-actions" style={{ justifyContent: "space-between", marginTop: 12 }}>
              <div className="rp-search-box" style={{ flex: "1 1 260px", maxWidth: 420 }}>
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search physicians"
                       placeholder="Search name, specialty, city, NPI" autoComplete="off" />
              </div>
              <button type="button" className="rp-btn primary" onClick={() => setAdding(true)}>Add physician</button>
            </div>
            {message && <div className={`rp-banner${message.err ? " err" : ""}`} role="status">{message.text}</div>}
            {loading && rows.length === 0 ? (
              <p className="rp-hint">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="rp-hint">No physicians found.</p>
            ) : (
              <ul className="rp-list">
                {rows.map((row) => (
                  <li key={row.id} className="rp-row">
                    <div className="rp-row-main">
                      <span className="rp-result-name">{row.doctor_name}</span>
                      {!row.active && <span className="rp-pill">Inactive</span>}
                      <span className="rp-result-sub">{physicianSubline(row) || "No details yet"}</span>
                    </div>
                    <div className="rp-row-btns">
                      <button type="button" className="rp-btn small" onClick={() => setEditing(row)}
                              disabled={busyId === row.id}>Edit</button>
                      <button type="button" className={`rp-btn small${row.active ? " danger" : ""}`}
                              onClick={() => toggle(row)} disabled={busyId === row.id}>
                        {row.active ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AddPhysicianDialog open={!!editing} mode="edit" physician={editing} access={access}
                          onClose={() => setEditing(null)} onSaved={saved} />
      <AddPhysicianDialog open={adding} mode="add" access={access}
                          onClose={() => setAdding(false)} onSaved={saved} />
    </>
  );
}
